import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apikeyDeleteApiKeyMutation, apikeyListApiKeysQueryKey } from '@column/api';

import { APIKey } from '../utils';
import { ROUTE } from '~/app/routes';
import { useDialog } from '~/components/Dialog/Provider';
import { getErrorMessage } from '~/lib/api/getErrorMessage';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';

interface UseDeleteAPIKeyAlertOptions {
  platformId: string;
  redirectOnDelete?: boolean;
}

type DeleteAPIKeyEntry = Pick<APIKey, 'id'> & Partial<Pick<APIKey, 'name'>>;

export const useDeleteAPIKeyAlert = ({ platformId, redirectOnDelete }: UseDeleteAPIKeyAlertOptions) => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { openAlert } = useDialog();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deleteAPIKey = useMutation(apikeyDeleteApiKeyMutation());

  return useCallback(
    (entry: DeleteAPIKeyEntry) => {
      openAlert({
        headline: 'Delete API Key',
        content: 'This action cannot be undone.',
        confirmButton: {
          label: 'Delete',
          variant: 'danger',
        },
        onSuccess: () => {
          if (!platformId) {
            return;
          }

          deleteAPIKey
            .mutateAsync({
              path: {
                api_key_id: entry.id,
                platform_id: platformId,
              },
            })
            .then(() => {
              queryClient.invalidateQueries({
                queryKey: apikeyListApiKeysQueryKey({
                  path: { platform_id: platformId },
                }),
              });
              addSuccessNotification({
                content: entry.name ? `${entry.name} deleted` : 'API key deleted',
              });

              if (redirectOnDelete) {
                navigate(ROUTE.PLATFORM_API_KEYS);
              }
            })
            .catch((error) => {
              addDangerNotification({
                content: getErrorMessage(error),
              });
            });
        },
      });
    },
    [
      addDangerNotification,
      addSuccessNotification,
      deleteAPIKey,
      navigate,
      openAlert,
      platformId,
      queryClient,
      redirectOnDelete,
    ]
  );
};
